import React, { useState, useEffect } from 'react';
import { sendInvitation, fetchOrganizationInvitations, removeOrganizationInvitation } from '../../Utils/functions';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';

const ManageInvitations = ({ organizationId }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('MEMBER');
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [inviteMessage, setInviteMessage] = useState('');
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [invitationToRemove, setInvitationToRemove] = useState(null);

  const fetchInvitations = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchOrganizationInvitations(organizationId);
      setInvitations(data);
    } catch (err) {
      setError('Failed to fetch invitations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchInvitations();
    }
  }, [organizationId]);

  const handleInviteClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirmInvite = async () => {
    setConfirmOpen(false);
    setInviteMessage('');
    setError(null);

    try {
      const response = await sendInvitation(organizationId, email, role);
      setInviteMessage(response.message);
      setEmail('');
      setRole('MEMBER');
      fetchInvitations(); // Refresh the list after sending invitation
    } catch (err) {
      setError(err.message || 'Failed to send invitation.');
    }
  };

  const handleCancelInvite = () => {
    setConfirmOpen(false);
  };

  const handleRemoveClick = (invitationId) => {
    setInvitationToRemove(invitationId);
    setRemoveConfirmOpen(true);
  };

  const handleConfirmRemove = async () => {
    setRemoveConfirmOpen(false);
    setError(null);
    try {
      await removeOrganizationInvitation(invitationToRemove);
      setInviteMessage('Invitation removed successfully.');
      fetchInvitations();
    } catch (err) {
      setError(err.message || 'Failed to remove invitation.');
    } finally {
      setInvitationToRemove(null);
    }
  };

  const handleCancelRemove = () => {
    setRemoveConfirmOpen(false);
    setInvitationToRemove(null);
  };

  return (
    <Box sx={{ p: 1 }}>
      <Box component="form" onSubmit={(e) => e.preventDefault()} sx={{ display: 'flex', gap: 2, alignItems: 'center' , p:1}}>
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          size="small"
          sx={{ flexGrow: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Role</InputLabel>
          <Select
            value={role}
            label="Role"
            onChange={(e) => setRole(e.target.value)}
          >
            <MenuItem value="MEMBER">Member</MenuItem>
            <MenuItem value="ADMIN">Admin</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleInviteClick} disabled={!email}>
          Invite
        </Button>
      </Box>

      {inviteMessage && <Alert severity="success" sx={{ mb: 2 }}>{inviteMessage}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <h3>Invited Users</h3>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : invitations.length === 0 ? (
        <Typography variant="body1">No invitations found.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Expires At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invitations.map((invitation) => (
                <TableRow key={invitation.id}>
                  <TableCell>{invitation.email}</TableCell>
                  <TableCell>{invitation.role}</TableCell>
                  <TableCell>{invitation.status}</TableCell>
                  <TableCell>{new Date(invitation.expiresAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => handleRemoveClick(invitation.id)}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={confirmOpen}
        onClose={handleCancelInvite}
        aria-labelledby="confirm-invite-dialog-title"
        aria-describedby="confirm-invite-dialog-description"
      >
        <DialogTitle id="confirm-invite-dialog-title">
          {"Confirm Invitation"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-invite-dialog-description">
            Are you sure you want to invite {email} as a {role}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelInvite}>Cancel</Button>
          <Button onClick={handleConfirmInvite} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={removeConfirmOpen}
        onClose={handleCancelRemove}
        aria-labelledby="confirm-remove-dialog-title"
        aria-describedby="confirm-remove-dialog-description"
      >
        <DialogTitle id="confirm-remove-dialog-title">
          {"Confirm Removal"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-remove-dialog-description">
            Are you sure you want to remove this invitation?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemove}>Cancel</Button>
          <Button onClick={handleConfirmRemove} color="error" autoFocus>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageInvitations;